import { Shell } from "@/components/layout/Shell";
import { ContaAsaasRepasses } from "@/components/configuracoes/ContaAsaasRepasses";
import { PacotesCreditos } from "@/components/configuracoes/PacotesCreditos";
import { useAuth } from "@/hooks/use-auth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import {
  Loader2,
  Settings2,
  Calendar,
  ClipboardList,
  FileText,
  Users,
  AlertTriangle,
  Gift,
  Receipt,
  Package,
  DollarSign,
  Trash2,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useModulos } from "@/hooks/use-modulos";

interface ConfiguracoesData {
  fechamentoAutomatico: boolean;
  artMultipla: boolean;
  artPorRt: boolean;
  artVistoriadora: string | null;
  vencimentoArtVistoriadora: string | null;
  laudosGratisPorCliente: number;
  fecharLotePorCliente: boolean;
  modeFaturamento: string;
  faturamentoAcumularX: number;
  modeLote: string;
  inadimplenciaBloqueioImediato: boolean;
  inadimplenciaDiasAtraso: number;
  inadimplenciaDiasBloqueio: number;
}

interface ConfigPreco {
  id: string;
  vistoriadoraId: string;
  parceiroId: string | null;
  valorPorLaudo: string | null;
  valoresPorTipo: Record<string, number> | null;
}

const TIPOS_VEICULO = [
  { key: "auto_leve", label: "Auto Leve" },
  { key: "moto", label: "Moto" },
  { key: "caminhao", label: "Caminhão" },
  { key: "onibus", label: "Ônibus" },
];

export default function Configuracoes() {
  const { profile, session } = useAuth();
  const { toast } = useToast();
  const isAdmin = profile?.roles?.includes("admin") ?? false;
  const { modulos } = useModulos();

  const { data: config, isLoading } = useQuery({
    queryKey: ["/api/configuracoes"],
    queryFn: async () => {
      const res = await fetch("/api/configuracoes", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Erro ao carregar configurações");
      return res.json() as Promise<ConfiguracoesData>;
    },
    enabled: !!session,
  });

  const [fechamentoAutomatico, setFechamentoAutomatico] = useState<
    boolean | null
  >(null);
  const [artMultipla, setArtMultipla] = useState<boolean | null>(null);
  const [artPorRt, setArtPorRt] = useState<boolean | null>(null);
  const [artVistoriadora, setArtVistoriadora] = useState<string>("");
  const [vencimentoArtVistoriadora, setVencimentoArtVistoriadora] =
    useState<string>("");
  const [laudosGratis, setLaudosGratis] = useState<number | null>(null);
  const [fecharPorCliente, setFecharPorCliente] = useState<boolean | null>(
    null,
  );
  const [showUnicaWarning, setShowUnicaWarning] = useState(false);
  const [pendingUnicaConfirm, setPendingUnicaConfirm] = useState(false);
  const [modeFaturamento, setModeFaturamento] = useState<string | null>(null);
  const [faturamentoAcumularX, setFaturamentoAcumularX] = useState<number | null>(null);
  const [inadimplenciaBloqueioImediato, setInadimplenciaBloqueioImediato] = useState<boolean | null>(null);
  const [inadimplenciaDiasAtraso, setInadimplenciaDiasAtraso] = useState<number | null>(null);
  const [inadimplenciaDiasBloqueio, setInadimplenciaDiasBloqueio] = useState<number | null>(null);
  const [modeLote, setModeLote] = useState<string | null>(null);
  const [novoPrecoParceiroId, setNovoPrecoParceiroId] = useState<string>("");
  const [novoPrecoTipos, setNovoPrecoTipos] = useState<Record<string, string>>({
    auto_leve: "", moto: "", caminhao: "", onibus: "",
  });

  const { data: precos, refetch: refetchPrecos } = useQuery({
    queryKey: ["/api/precos-vistoriadora"],
    queryFn: async () => {
      const res = await fetch("/api/precos-vistoriadora", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) return [] as ConfigPreco[];
      return res.json() as Promise<ConfigPreco[]>;
    },
    enabled: !!session && isAdmin,
  });

  const { data: parceiros } = useQuery({
    queryKey: ["/api/veiculos/parceiros/lista"],
    queryFn: async () => {
      const res = await fetch("/api/veiculos/parceiros/lista", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) return [] as any[];
      return res.json() as Promise<any[]>;
    },
    enabled: !!session && isAdmin,
  });

  const salvarPreco = async () => {
    const valoresPorTipo: Record<string, number> = {};
    for (const { key } of TIPOS_VEICULO) {
      const v = parseFloat(novoPrecoTipos[key] ?? "");
      if (!isNaN(v) && v > 0) valoresPorTipo[key] = v;
    }
    if (Object.keys(valoresPorTipo).length === 0) return;
    const res = await fetch("/api/precos-vistoriadora", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify({
        parceiroId: novoPrecoParceiroId || null,
        valoresPorTipo,
        valorPorLaudo: null,
      }),
    });
    if (res.ok) {
      refetchPrecos();
      setNovoPrecoTipos({ auto_leve: "", moto: "", caminhao: "", onibus: "" });
      setNovoPrecoParceiroId("");
      toast({ title: "Preço salvo com sucesso" });
    }
  };

  const excluirPreco = async (id: string) => {
    const res = await fetch(`/api/precos-vistoriadora/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });
    if (res.ok) refetchPrecos();
  };

  // Valores resolvidos (estado local tem precedência sobre server)
  const currentFechamento =
    fechamentoAutomatico !== null
      ? fechamentoAutomatico
      : (config?.fechamentoAutomatico ?? true);
  const currentArtMultipla =
    artMultipla !== null ? artMultipla : (config?.artMultipla ?? false);
  const currentArtPorRt =
    artPorRt !== null ? artPorRt : (config?.artPorRt ?? false);
  const currentLaudosGratis =
    laudosGratis !== null
      ? laudosGratis
      : (config?.laudosGratisPorCliente ?? 0);
  const currentFecharPorCliente =
    fecharPorCliente !== null
      ? fecharPorCliente
      : (config?.fecharLotePorCliente ?? false);
  const currentModeFaturamento =
    modeFaturamento !== null ? modeFaturamento : (config?.modeFaturamento ?? "por_laudo");
  const currentFaturamentoAcumularX =
    faturamentoAcumularX !== null ? faturamentoAcumularX : (config?.faturamentoAcumularX ?? 1);
  const currentModeLote =
    modeLote !== null ? modeLote : (config?.modeLote ?? "geral");
  const currentInadimplenciaBloqueioImediato =
    inadimplenciaBloqueioImediato !== null
      ? inadimplenciaBloqueioImediato
      : (config?.inadimplenciaBloqueioImediato ?? false);
  const currentInadimplenciaDiasAtraso =
    inadimplenciaDiasAtraso !== null
      ? inadimplenciaDiasAtraso
      : (config?.inadimplenciaDiasAtraso ?? 1);
  const currentInadimplenciaDiasBloqueio =
    inadimplenciaDiasBloqueio !== null
      ? inadimplenciaDiasBloqueio
      : (config?.inadimplenciaDiasBloqueio ?? 30);

  // Inicializa campos de ART com valores do servidor quando carregam
  const artValue = artVistoriadora || config?.artVistoriadora || "";
  const vencimentoValue =
    vencimentoArtVistoriadora || config?.vencimentoArtVistoriadora || "";

  const mutation = useMutation({
    mutationFn: async (payload: {
      fechamentoAutomatico: boolean;
      artMultipla: boolean;
      artPorRt: boolean;
      art?: string;
      vencimentoArt?: string;
      laudosGratisPorCliente?: number;
      fecharLotePorCliente?: boolean;
    }) => {
      const res = await fetch("/api/configuracoes", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Erro ao salvar configuração");
      }
      return res.json() as Promise<ConfiguracoesData>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/configuracoes"] });
      toast({ title: "Configurações salvas com sucesso" });
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const handleSave = () => {
    // Ao ativar ART Múltipla, garante que a ART foi preenchida
    if (currentArtMultipla) {
      const finalArt = artVistoriadora || config?.artVistoriadora;
      if (!finalArt) {
        toast({
          title:
            "Preencha o número da ART antes de ativar o modo ART Múltipla.",
          variant: "destructive",
        });
        return;
      }
    }
    mutation.mutate({
      fechamentoAutomatico: currentFechamento,
      artMultipla: currentArtMultipla,
      artPorRt: currentArtMultipla ? false : currentArtPorRt,
      art: artVistoriadora || undefined,
      vencimentoArt: vencimentoArtVistoriadora || undefined,
      laudosGratisPorCliente: currentLaudosGratis,
      fecharLotePorCliente: currentFecharPorCliente,
      modeFaturamento: currentModeFaturamento,
      faturamentoAcumularX: currentFaturamentoAcumularX,
      modeLote: currentModeLote,
      inadimplenciaBloqueioImediato: currentInadimplenciaBloqueioImediato,
      inadimplenciaDiasAtraso: currentInadimplenciaDiasAtraso,
      inadimplenciaDiasBloqueio: currentInadimplenciaDiasBloqueio,
    } as any);
  };

  const handleArtTipoChange = (value: string) => {
    const isMultipla = value === "multipla";
    // Se está trocando de múltipla para única, avisa sobre parceiros sem ART
    if (!isMultipla && (artMultipla ?? config?.artMultipla)) {
      setShowUnicaWarning(true);
      setPendingUnicaConfirm(true);
      return;
    }
    setArtMultipla(isMultipla);
    if (isMultipla) setArtPorRt(false);
  };

  const confirmSwitchToUnica = () => {
    setArtMultipla(false);
    setShowUnicaWarning(false);
    setPendingUnicaConfirm(false);
  };

  if (isLoading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-display text-slate-900">
          Configurações
        </h1>
        <p className="text-muted-foreground mt-1">
          Personalize o comportamento do sistema para sua empresa
        </p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Fechamento de Lotes */}
        <Card className="border-none shadow-md">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Settings2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Fechamento de Lotes</CardTitle>
                <CardDescription className="text-sm mt-0.5">
                  Define como os lotes mensais de laudos são fechados e
                  faturados
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={currentFechamento ? "automatico" : "manual"}
              onValueChange={(v) => setFechamentoAutomatico(v === "automatico")}
              disabled={!isAdmin}
              className="space-y-4"
            >
              <div
                className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${currentFechamento ? "border-primary bg-primary/5" : "border-slate-200"}`}
              >
                <RadioGroupItem
                  value="automatico"
                  id="automatico"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label
                    htmlFor="automatico"
                    className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800"
                  >
                    <Calendar className="w-4 h-4 text-primary" />
                    Automático (padrão)
                  </Label>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    O lote fecha automaticamente 1 mês após o primeiro laudo
                    finalizado naquele mês. A fatura é gerada automaticamente ao
                    fechar.
                  </p>
                </div>
              </div>

              <div
                className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${!currentFechamento ? "border-primary bg-primary/5" : "border-slate-200"}`}
              >
                <RadioGroupItem value="manual" id="manual" className="mt-0.5" />
                <div className="flex-1">
                  <Label
                    htmlFor="manual"
                    className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800"
                  >
                    <ClipboardList className="w-4 h-4 text-amber-600" />
                    Manual
                  </Label>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    Você escolhe quando fechar o lote e quais laudos fazem
                    parte. Ao fechar, pode informar um número de{" "}
                    <strong>Ordem de Serviço</strong>. A fatura é gerada
                    imediatamente com vencimento de{" "}
                    <strong>3 dias úteis</strong>.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* ART Múltipla — card original, sem alterações */}
        {modulos.artMultipla && (
          <Card className="border-none shadow-md">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-base">
                    ART — Anotação de Responsabilidade Técnica
                  </CardTitle>
                  <CardDescription className="text-sm mt-0.5">
                    Define quem é responsável pela ART dos laudos emitidos
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup
                value={currentArtMultipla ? "multipla" : "unica"}
                onValueChange={handleArtTipoChange}
                disabled={!isAdmin}
                className="space-y-4"
              >
                <div
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${!currentArtMultipla ? "border-primary bg-primary/5" : "border-slate-200"}`}
                >
                  <RadioGroupItem value="unica" id="art-unica" className="mt-0.5" />
                  <div className="flex-1">
                    <Label
                      htmlFor="art-unica"
                      className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800"
                    >
                      <Users className="w-4 h-4 text-primary" />
                      ART Única (padrão)
                    </Label>
                    <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                      Cada parceiro de negócio é responsável por sua própria ART
                      e data de validade. Laudos são bloqueados quando a ART do
                      parceiro estiver expirada ou ausente.
                    </p>
                  </div>
                </div>

                <div
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${currentArtMultipla ? "border-primary bg-primary/5" : "border-slate-200"}`}
                >
                  <RadioGroupItem value="multipla" id="art-multipla" className="mt-0.5" />
                  <div className="flex-1">
                    <Label
                      htmlFor="art-multipla"
                      className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800"
                    >
                      <FileText className="w-4 h-4 text-emerald-600" />
                      ART Múltipla
                    </Label>
                    <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                      Sua vistoriadora possui uma ART que vale para todos os
                      seus parceiros de negócio. A ART e validade da
                      vistoriadora são usadas na validação de todos os laudos.
                    </p>
                  </div>
                </div>
              </RadioGroup>

              {currentArtMultipla && (
                <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100 space-y-4">
                  <p className="text-sm font-medium text-emerald-800">ART da Vistoriadora</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="art-numero" className="text-sm text-slate-700">
                        Número da ART
                      </Label>
                      <Input
                        id="art-numero"
                        placeholder="Ex: 123456789"
                        value={artVistoriadora || config?.artVistoriadora || ""}
                        onChange={(e) => setArtVistoriadora(e.target.value)}
                        disabled={!isAdmin}
                        className="bg-white"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="art-vencimento" className="text-sm text-slate-700">
                        Validade
                      </Label>
                      <Input
                        id="art-vencimento"
                        type="date"
                        value={vencimentoArtVistoriadora || config?.vencimentoArtVistoriadora || ""}
                        onChange={(e) => setVencimentoArtVistoriadora(e.target.value)}
                        disabled={!isAdmin}
                        className="bg-white"
                      />
                    </div>
                  </div>
                  {!artValue && (
                    <p className="text-xs text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Preencha a ART para ativar o modo Múltipla.
                    </p>
                  )}
                </div>
              )}

              {!isAdmin && (
                <p className="text-xs text-muted-foreground mt-2">
                  Apenas administradores podem alterar as configurações de ART.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Vínculo da ART — card independente: ART por Parceiro de Negócio ou por RT */}
        <Card className="border-none shadow-md">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Vínculo da ART</CardTitle>
                <CardDescription className="text-sm mt-0.5">
                  Define a quem a ART pertence: ao Parceiro de Negócio ou ao
                  Responsável Técnico
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup
              value={currentArtPorRt ? "rt" : "parceiro"}
              onValueChange={(v) => setArtPorRt(v === "rt")}
              disabled={!isAdmin}
              className="space-y-4"
            >
              <div
                className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${!currentArtPorRt ? "border-primary bg-primary/5" : "border-slate-200"}`}
              >
                <RadioGroupItem value="parceiro" id="vinculo-parceiro" className="mt-0.5" />
                <div className="flex-1">
                  <Label
                    htmlFor="vinculo-parceiro"
                    className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800"
                  >
                    <Users className="w-4 h-4 text-primary" />
                    ART por Parceiro de Negócio
                  </Label>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    Cada parceiro de negócio cadastra e mantém sua própria ART.
                    Os campos de ART aparecem no cadastro da empresa parceira.
                  </p>
                </div>
              </div>

              <div
                className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${currentArtPorRt ? "border-primary bg-primary/5" : "border-slate-200"}`}
              >
                <RadioGroupItem value="rt" id="vinculo-rt" className="mt-0.5" />
                <div className="flex-1">
                  <Label
                    htmlFor="vinculo-rt"
                    className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800"
                  >
                    <FileText className="w-4 h-4 text-blue-600" />
                    ART por Responsável Técnico (padrão)
                  </Label>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    Cada RT cadastra sua própria ART diretamente no seu perfil,
                    acessível na página Usuários no card do RT.
                  </p>
                </div>
              </div>
            </RadioGroup>

            {!isAdmin && (
              <p className="text-xs text-muted-foreground mt-2">
                Apenas administradores podem alterar esta configuração.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Laudos por Associados/Mês — visível apenas para o Owner */}
        {modulos.laudosGratisPorCliente > 0 &&
          profile?.empresa?.tipo === "OWNER" && (
            <Card className="border-none shadow-md">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">
                      Laudos por Associados/Mês
                    </CardTitle>
                    <CardDescription className="text-sm mt-0.5">
                      Quantidade de laudos que o parceiro pode solicitar por
                      conta própria por mês. Acima do limite, apenas Admin ou RT
                      podem autorizar novos laudos.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-32">
                    <Input
                      type="number"
                      min={0}
                      value={currentLaudosGratis}
                      onChange={(e) =>
                        setLaudosGratis(
                          Math.max(0, parseInt(e.target.value) || 0),
                        )
                      }
                      disabled={!isAdmin}
                      className="text-center font-semibold"
                    />
                  </div>
                  <p className="text-sm text-slate-500">
                    {currentLaudosGratis === 0
                      ? "Módulo inativo — parceiros podem solicitar laudos sem limite."
                      : `Parceiros podem solicitar até ${currentLaudosGratis} laudo${currentLaudosGratis > 1 ? "s" : ""}/mês. Acima disso, precisam de autorização do Admin/RT.`}
                  </p>
                </div>
                {!isAdmin && (
                  <p className="text-xs text-muted-foreground">
                    Apenas administradores podem alterar esta configuração.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

        {/* Fechar Lote por Cliente */}
        {!currentFechamento && (
          <Card className="border-none shadow-md">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <CardTitle className="text-base">
                    Fechar Lote por Cliente
                  </CardTitle>
                  <CardDescription className="text-sm mt-0.5">
                    Quando ativado, cada lote é fechado separadamente por
                    empresa-cliente.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 rounded-xl border-2 border-slate-200">
                <div>
                  <p className="font-semibold text-slate-800 text-sm">
                    Separar lotes por cliente
                  </p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {currentFecharPorCliente
                      ? "Ativado — cada cliente terá seu próprio lote ao fechar."
                      : "Desativado — todos os laudos são agrupados em um único lote."}
                  </p>
                </div>
                <Switch
                  checked={currentFecharPorCliente}
                  onCheckedChange={setFecharPorCliente}
                  disabled={!isAdmin}
                />
              </div>
              {!isAdmin && (
                <p className="text-xs text-muted-foreground mt-3">
                  Apenas administradores podem alterar esta configuração.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Agrupamento de Lotes */}
        <Card className="border-none shadow-md">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                <Package className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-base">Agrupamento de Lotes</CardTitle>
                <CardDescription className="text-sm mt-0.5">
                  Define se os laudos são agrupados em um lote geral ou em lotes separados por parceiro
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={currentModeLote}
              onValueChange={setModeLote}
              disabled={!isAdmin}
              className="space-y-3"
            >
              <div className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${currentModeLote === "geral" ? "border-primary bg-primary/5" : "border-slate-200"}`}>
                <RadioGroupItem value="geral" id="lote-geral" className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="lote-geral" className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800">
                    <Package className="w-4 h-4 text-primary" />
                    Lote Geral (padrão)
                  </Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Todos os laudos finalizados entram no mesmo lote aberto, independente do parceiro.
                  </p>
                </div>
              </div>
              <div className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${currentModeLote === "por_parceiro" ? "border-primary bg-primary/5" : "border-slate-200"}`}>
                <RadioGroupItem value="por_parceiro" id="lote-parceiro" className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="lote-parceiro" className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800">
                    <Users className="w-4 h-4 text-indigo-600" />
                    Lote por Parceiro
                  </Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Cada parceiro tem seu próprio lote. Se 2 parceiros finalizarem laudos, serão 2 lotes distintos.
                  </p>
                </div>
              </div>
            </RadioGroup>
            {!isAdmin && (
              <p className="text-xs text-muted-foreground mt-3">
                Apenas administradores podem alterar esta configuração.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Modo de Faturamento */}
        <Card className="border-none shadow-md">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-base">Modo de Faturamento para Parceiros</CardTitle>
                <CardDescription className="text-sm mt-0.5">
                  Quando gerar fatura para o parceiro de negócio
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={currentModeFaturamento}
              onValueChange={(v) => {
                setModeFaturamento(v);
                // "X Laudos" e "Por Lote" só fazem sentido com fatura por parceiro (não dá pra
                // fechar um lote geral quando o gatilho é o total/fechamento de cada parceiro).
                if (v === "acumular_x" || v === "por_lote") setModeLote("por_parceiro");
              }}
              disabled={!isAdmin}
              className="space-y-3"
            >
              <div className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${currentModeFaturamento === "por_laudo" ? "border-primary bg-primary/5" : "border-slate-200"}`}>
                <RadioGroupItem value="por_laudo" id="fat-laudo" className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="fat-laudo" className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800">
                    <FileText className="w-4 h-4 text-primary" />
                    Por Laudo (padrão)
                  </Label>
                  <p className="text-sm text-slate-500 mt-1">Cada laudo finalizado gera uma fatura automaticamente para o parceiro.</p>
                </div>
              </div>
              <div className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${currentModeFaturamento === "acumular_x" ? "border-primary bg-primary/5" : "border-slate-200"}`}>
                <RadioGroupItem value="acumular_x" id="fat-acumular" className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="fat-acumular" className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800">
                    <ClipboardList className="w-4 h-4 text-amber-600" />
                    Acumular X Laudos
                  </Label>
                  <p className="text-sm text-slate-500 mt-1">Gera a fatura somente quando o parceiro acumular X laudos.</p>
                  {currentModeFaturamento === "acumular_x" && (
                    <div className="mt-3 flex items-center gap-2">
                      <Label className="text-sm text-slate-700 shrink-0">Quantidade:</Label>
                      <Input
                        type="number"
                        min={1}
                        className="w-24 text-center"
                        value={currentFaturamentoAcumularX}
                        onChange={(e) => setFaturamentoAcumularX(Math.max(1, parseInt(e.target.value) || 1))}
                        disabled={!isAdmin}
                      />
                      <span className="text-sm text-slate-500">laudos</span>
                    </div>
                  )}
                </div>
              </div>
              <div className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${currentModeFaturamento === "por_lote" ? "border-primary bg-primary/5" : "border-slate-200"}`}>
                <RadioGroupItem value="por_lote" id="fat-lote" className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="fat-lote" className="flex items-center gap-2 cursor-pointer font-semibold text-slate-800">
                    <Package className="w-4 h-4 text-indigo-600" />
                    Por Lote
                  </Label>
                  <p className="text-sm text-slate-500 mt-1">Gera a fatura apenas quando o lote do parceiro for fechado.</p>
                </div>
              </div>
            </RadioGroup>
            {!isAdmin && (
              <p className="text-xs text-muted-foreground mt-3">
                Apenas administradores podem alterar esta configuração.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Regras de Inadimplência */}
        <Card className="border-none shadow-md">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-base">Regras de Inadimplência</CardTitle>
                <CardDescription className="text-sm mt-0.5">
                  O que acontece quando uma fatura vence sem pagamento
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl border-2 border-slate-200">
              <div>
                <p className="font-semibold text-slate-800 text-sm">Bloquear imediatamente ao vencer</p>
                <p className="text-xs text-slate-500 mt-0.5">Ao vencer sem pagamento, o parceiro já fica bloqueado de solicitar laudos</p>
              </div>
              <Switch
                checked={currentInadimplenciaBloqueioImediato}
                onCheckedChange={(v) => setInadimplenciaBloqueioImediato(v)}
                disabled={!isAdmin}
              />
            </div>
            {!currentInadimplenciaBloqueioImediato && (
              <div className="space-y-3 p-4 rounded-xl border-2 border-slate-200">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Período de tolerância</p>
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-slate-700 w-40 shrink-0">Dias até "Atrasada":</Label>
                  <Input
                    type="number"
                    min={1}
                    max={currentInadimplenciaDiasBloqueio - 1}
                    className="w-24 text-center"
                    value={currentInadimplenciaDiasAtraso}
                    onChange={(e) =>
                      setInadimplenciaDiasAtraso(Math.max(1, parseInt(e.target.value) || 1))
                    }
                    disabled={!isAdmin}
                  />
                  <span className="text-sm text-slate-500">dias após vencimento</span>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-slate-700 w-40 shrink-0">Dias até "Bloqueada":</Label>
                  <Input
                    type="number"
                    min={currentInadimplenciaDiasAtraso + 1}
                    className="w-24 text-center"
                    value={currentInadimplenciaDiasBloqueio}
                    onChange={(e) =>
                      setInadimplenciaDiasBloqueio(
                        Math.max(currentInadimplenciaDiasAtraso + 1, parseInt(e.target.value) || 30),
                      )
                    }
                    disabled={!isAdmin}
                  />
                  <span className="text-sm text-slate-500">dias após vencimento</span>
                </div>
                <p className="text-xs text-slate-400">
                  Exemplo: fatura vence hoje → fica "Atrasada" no dia {currentInadimplenciaDiasAtraso} e "Bloqueada" no dia {currentInadimplenciaDiasBloqueio}.
                </p>
              </div>
            )}
            {!isAdmin && (
              <p className="text-xs text-muted-foreground">
                Apenas administradores podem alterar esta configuração.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Preços por Parceiro */}
        {isAdmin && profile?.empresa?.tipo === "VISTORIADORA" && <PacotesCreditos />}
        {isAdmin && (
          <Card className="border-none shadow-md">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Preços por Parceiro</CardTitle>
                  <CardDescription className="text-sm mt-0.5">
                    Valor que sua vistoriadora cobra por laudo para cada parceiro. O preço padrão (sem parceiro) vale para todos que não tiverem preço específico.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tabela de preços existentes */}
              {precos && precos.length > 0 && (
                <div className="space-y-2">
                  {precos.map((p) => {
                    const parceiro = parceiros?.find((pc: any) => pc.id === p.parceiroId);
                    return (
                      <div key={p.id} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-semibold text-slate-800">
                            {p.parceiroId ? (parceiro?.nomeFantasia ?? parceiro?.razaoSocial ?? "Parceiro") : "⭐ Padrão (todos os parceiros)"}
                          </p>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                            onClick={() => excluirPreco(p.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        {p.valoresPorTipo ? (
                          <div className="grid grid-cols-2 gap-1">
                            {TIPOS_VEICULO.map(({ key, label }) =>
                              p.valoresPorTipo![key] != null ? (
                                <p key={key} className="text-xs text-slate-500">
                                  {label}: <span className="font-semibold text-slate-700">R$ {Number(p.valoresPorTipo![key]).toFixed(2)}</span>
                                </p>
                              ) : null
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500">R$ {Number(p.valorPorLaudo ?? 0).toFixed(2)} / laudo</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Formulário para adicionar preço por tipo */}
              <div className="p-3 rounded-lg border border-dashed border-slate-300 space-y-3">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Adicionar / atualizar preços por tipo</p>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm text-slate-700"
                  value={novoPrecoParceiroId}
                  onChange={(e) => setNovoPrecoParceiroId(e.target.value)}
                >
                  <option value="">⭐ Padrão (todos os parceiros)</option>
                  {parceiros?.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.nomeFantasia ?? p.razaoSocial}
                    </option>
                  ))}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  {TIPOS_VEICULO.map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-1.5">
                      <Label className="text-xs text-slate-600 w-20 shrink-0">{label}</Label>
                      <div className="flex items-center gap-1 flex-1">
                        <span className="text-xs text-slate-400">R$</span>
                        <Input
                          type="number"
                          min={0}
                          step={0.01}
                          placeholder="0,00"
                          className="h-7 text-sm text-right"
                          value={novoPrecoTipos[key] ?? ""}
                          onChange={(e) => setNovoPrecoTipos((prev) => ({ ...prev, [key]: e.target.value }))}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  size="sm"
                  onClick={salvarPreco}
                  disabled={TIPOS_VEICULO.every(({ key }) => !novoPrecoTipos[key])}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Salvar Preços
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <ContaAsaasRepasses
            token={session?.access_token}
            ambienteInicial={profile?.empresa?.modoTeste ? "simulado" : "producao"}
          />
        )}

        {isAdmin && (
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Salvar Configurações
            </Button>
          </div>
        )}
      </div>

      {/* Diálogo de aviso ao trocar de Múltipla para Única */}
      <AlertDialog open={showUnicaWarning} onOpenChange={setShowUnicaWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Atenção: Parceiros sem ART serão bloqueados
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 leading-relaxed">
              Ao mudar para <strong>ART Única</strong>, cada parceiro de negócio
              precisará ter sua própria ART cadastrada para solicitar laudos.
              Parceiros sem ART serão bloqueados até que cadastrem a ART.
              <br />
              <br />
              Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowUnicaWarning(false);
                setPendingUnicaConfirm(false);
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmSwitchToUnica}>
              Sim, mudar para ART Única
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
